<template>
  <el-row justify="center">
    <el-col :xs="24" :sm="22" :md="20" :lg="18">

      <el-card shadow="never" class="mb-4">
        <el-row justify="space-between" align="middle">
          <el-col :md="12">
            <h4 class="mb-0">会员通讯录</h4>
            <p class="text-muted mb-0">浏览和搜索研究会会员</p>
          </el-col>
          <el-col :md="12" class="mt-3 mt-md-0">
            <el-input
              v-model="searchTerm"
              placeholder="按姓名、专业、工作单位搜索..."
              size="large"
              clearable
              @keydown.enter="executeSearch"
            >
              <template #append>
                <el-button @click="executeSearch" :loading="isLoading">
                  <i class="fas fa-search"></i>
                </el-button>
              </template>
            </el-input>
          </el-col>
        </el-row>
      </el-card>

      <el-alert v-if="requestError" :title="requestError" type="error" class="mb-3" show-icon />
      <el-alert v-if="requestSuccess" :title="requestSuccess" type="success" class="mb-3" show-icon />

      <el-card shadow="never" v-loading="isLoading">

        <el-empty v-if="!hasSearched" description="请在上方输入关键词开始搜索。" />

        <el-empty v-else-if="hasSearched && members.length === 0" description="未找到匹配的会员。" />

        <el-row :gutter="20" v-else>
          <el-col :md="12" v-for="member in members" :key="member.id" class="mb-4">
            <el-card shadow="hover" class="h-100 member-card">
              <div class="card-content">
                <div class="flex-grow-1">
                  <h5 class="card-title text-primary">{{ member.full_name }}</h5>
                  <h6 class="card-subtitle mb-2 text-muted">{{ member.member_type }}</h6>
                  <el-divider />
                  <p class="card-text">
                    <strong>专业:</strong> {{ member.specialty || '未填写' }}
                  </p>
                  <p class="card-text">
                    <strong>单位/职务:</strong> {{ member.organization_and_title || '未填写' }}
                  </p>
                </div>

                <div class="mt-3" v-if="isLoggedInMember">
                  <el-button
                    type="primary"
                    plain
                    @click="handleRequestContact(member)"
                    :loading="isRequesting[member.id]">
                    {{ isRequesting[member.id] ? '请求中...' : '请求联系' }}
                  </el-button>
                </div>

              </div>
            </el-card>
          </el-col>
        </el-row>

        <div class="d-flex justify-content-center mt-4" v-if="hasSearched && totalMembers > pageSize">
          <el-pagination
            background
            layout="prev, pager, next"
            :total="totalMembers"
            :page-size="pageSize"
            :current-page="currentPage"
            @current-change="handlePageChange"
          />
        </div>

      </el-card>

    </el-col>
  </el-row>
</template>

<script>
import axios from 'axios';
// 【V4.0 新增】导入 Element Plus 消息提示
import { ElMessageBox, ElMessage } from 'element-plus';

export default {
  name: 'DirectoryView',
  data() {
    return {
      isLoading: false,
      hasSearched: false,
      members: [],
      searchTerm: '',

      // --- 【V4.0 修改】 分页状态 ---
      totalMembers: 0,  // 总条目数
      pageSize: 5,      // (与 settings.py 中的 PAGE_SIZE 保持一致)
      currentPage: 1,   // 当前页码
      // (我们不再需要 nextPageUrl 和 prevPageUrl)

      // V2.0 状态
      isFeePaid: localStorage.getItem('isFeePaid') === 'true',
      isLoggedInMember: !!localStorage.getItem('authToken'),
      isRequesting: {},
      requestError: null,
      requestSuccess: null,
    };
  },

  mounted() {
    // 页面加载时, 只检查 token (不自动加载列表)
    const token = localStorage.getItem('authToken');
    if (!token) {
      this.$router.push('/login');
    }
  },

  methods: {
    /**
     * V1.0: 获取会员列表 (分页和搜索)
     */
    async loadPage() {
      this.isLoading = true;
      this.hasSearched = true;
      this.requestError = null;
      this.requestSuccess = null;
      const token = localStorage.getItem('authToken');

      // 【V4.0 修改】使用 params 对象
      const params = {
        page: this.currentPage, // 告诉后端要第几页
      };
      if (this.searchTerm.trim()) {
        params.search = this.searchTerm;
      }

      try {
        const response = await axios.get('/members/api/directory/', {
          headers: { 'Authorization': `Token ${token}` },
          params: params // (axios 会自动处理 ?page=1&search=...)
        });

        // 【V4.0 修改】保存分页数据
        this.members = response.data.results;
        this.totalMembers = response.data.count; // 保存总数
        this.pageSize = 5; // (与 settings.py 一致)

      } catch (error) {
        console.error('获取会员通讯录失败:', error);
        if (error.response && error.response.status === 401) {
          this.$router.push('/login');
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * V1.0: 执行搜索
     */
    executeSearch() {
      this.currentPage = 1; // 每次新搜索都回到第1页
      this.loadPage();
    },

    /**
     * V4.0: 处理分页器点击
     */
    handlePageChange(newPage) {
      this.currentPage = newPage;
      this.loadPage(); // 加载新的一页
    },

    /**
     * V2.0: 处理“请求联系”按钮
     */
    async handleRequestContact(targetMember) {
      // 1. 清除旧消息
      this.requestError = null;
      this.requestSuccess = null;

      // 2. 付费墙检查
      if (!this.isFeePaid) {
        this.requestError = `此为高级会员专享功能。您尚未缴纳本年度会费。请您先前往【个人中心】缴纳年度会费，即可解锁联系权限。`;
        return;
      }

      // 3. 【V4.0 升级】使用 ElMessageBox.prompt 替换 prompt
      try {
        const { value: message } = await ElMessageBox.prompt(
          `您即将向 ${targetMember.full_name} 发送联系请求。请简单介绍您自己，以及您希望联系对方的事由：`,
          '发送联系请求',
          {
            confirmButtonText: '发送请求',
            cancelButtonText: '取消',
            inputPattern: /.+/, // (简单的“非空”验证)
            inputErrorMessage: '介绍消息不能为空。',
          }
        );

        // --- 4. 用户点击了“发送请求” ---
        if (message && message.trim() !== '') {
          this.isRequesting[targetMember.id] = true;
          const token = localStorage.getItem('authToken');

          try {
            const payload = {
              target: targetMember.id,
              message: message
            };
            await axios.post('/members/api/directory/request/', payload, {
              headers: { 'Authorization': `Token ${token}` }
            });
            this.requestSuccess = `已成功向 ${targetMember.full_name} 发送联系请求！对方同意后，系统将通过邮件通知您。`;

          } catch (error) {
            console.error('发送联系请求失败:', error);
            if (error.response && error.response.data && error.response.data.error) {
              this.requestError = `操作失败：${error.response.data.error}`;
            } else {
              this.requestError = '操作失败，请稍后再试。';
            }
          } finally {
            this.isRequesting[targetMember.id] = false;
          }
        }

      } catch (action) {
        // --- 5. 用户点击了“取消” ---
        if (action === 'cancel') {
          ElMessage({ type: 'info', message: '操作已取消。' });
        }
      }
    }
  },

  // (activated 和 watch 保持不变)
  activated() {
     this.isFeePaid = localStorage.getItem('isFeePaid') === 'true';
     this.isLoggedInMember = !!localStorage.getItem('authToken');
  },
  watch: {
    '$route'() {
       this.isFeePaid = localStorage.getItem('isFeePaid') === 'true';
       this.isLoggedInMember = !!localStorage.getItem('authToken');
    }
  }
};
</script>

<style scoped>
/* --- V4.0 样式 --- */
.h-100 {
  height: 100%;
}
.mb-4 {
  margin-bottom: 1.5rem;
}
.mt-3 {
  margin-top: 1rem;
}
.mb-0 {
  margin-bottom: 0;
}
.me-1 {
  margin-right: 0.25rem;
}
.text-muted {
  color: var(--el-text-color-secondary);
}
.text-primary {
  color: var(--el-color-primary);
}
.d-flex {
  display: flex;
}
.justify-content-center {
  justify-content: center;
}
.mt-md-0 {
  margin-top: 0 !important;
}

.member-card:hover {
  transform: translateY(-5px);
  box-shadow: var(--el-box-shadow-light);
}
.card-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.card-content .flex-grow-1 {
  flex-grow: 1;
}
.card-text {
  font-size: 0.95rem;
  color: var(--el-text-color-regular);
}
.card-text strong {
  color: var(--el-text-color-primary);
}
</style>